import React, { useEffect, useRef, useState } from 'react';
import { Wrapper } from './style';
import { Collapse, message, Select, Carousel, Spin } from 'antd';
import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { ConvertParams } from '../../../../config';
import { MakeApiCall } from '../../../../apis';
import ItemMaster from '../item-list';
import { getAmount } from '../../../../utils/helper';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    LineController,
    BarController,
    Title,
    Tooltip,
    Legend
);

const { Option } = Select;

const ChartComponent = ({
                            userData,
                            selectedMarketplace,
                            selectedCurrencySign,
                            periodicity,
                            otherRange,
                            selectedSku
                        }) => {

    const isInitialLoad = useRef(true);

    /** IMPORTANT: CACHE ONLY FOR IDENTICAL FILTERS */
    const fetchedSummaryKeysRef = useRef(new Set());
    const fetchedChartKeysRef = useRef(new Set());

    /** 🔑 Stable filter key generator */
    const buildFilterKey = (f) =>
        JSON.stringify({
            page: f.page,
            perPage: f['per-page'],
            marketplace_id: f.marketplace_id,
            report_type: f.report_type,
            start_date: f.start_date,
            end_date: f.end_date,
            sku: f.sku
        });

    /** Local filters */
    const [filters, setFilters] = useState({
        page: 1,
        'per-page': 20,
        report_type: periodicity,
        start_date: otherRange.start_date,
        end_date: otherRange.end_date,
        sku: selectedSku?.join(',')
    });

    const [loading, setLoading] = useState(true);
    const [tilesList, setTilesList] = useState([]);

    // chart-specific loading flag (new)
    const [chartLoading, setChartLoading] = useState(false);

    const [selectedPreset, setSelectedPreset] = useState(null);
    const [selectedParameterKeys, setSelectedParameterKeys] = useState([]);
    const [defaultPresets, setDefaultPresets] = useState({});
    const [parameterOptions, setParameterOptions] = useState([]);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [seriesDefinitions, setSeriesDefinitions] = useState([]);

    // -------------------------------------------------------------
    // 🔄 UPDATE FILTERS WHEN INPUT PROPS CHANGE — ONLY IF DIFFERENT
    // -------------------------------------------------------------
    useEffect(() => {
        if (!selectedMarketplace?.id) return;

        const newFilters = {
            page: 1,
            'per-page': 20,
            marketplace_id: selectedMarketplace.id,
            report_type: periodicity,
            start_date: otherRange.start_date,
            end_date: otherRange.end_date,
            sku: selectedSku?.join(',')
        };

        setFilters((prev) => {
            const prevStr = JSON.stringify(prev);
            const nextStr = JSON.stringify(newFilters);

            if (prevStr === nextStr) return prev;

            /** OPTION B: RESET CACHE WHEN **ANY FILTER** CHANGES */
            fetchedSummaryKeysRef.current = new Set();
            fetchedChartKeysRef.current = new Set();

            return newFilters;
        });

    }, [selectedMarketplace, periodicity, otherRange, selectedSku]);

    // -------------------------------------------------------------
    // Load Params Once
    // -------------------------------------------------------------
    useEffect(() => {
        fetchFilterData();
    }, []);

    // -------------------------------------------------------------
    // 🔥 Fetch Summary Data — Only Once Per Filter Key
    // -------------------------------------------------------------
    useEffect(() => {
        if (!filters.marketplace_id) return;

        const key = buildFilterKey(filters);

        if (fetchedSummaryKeysRef.current.has(key)) return;
        fetchedSummaryKeysRef.current.add(key);

        fetchProfitAndLossData();
    }, [filters]);

    // -------------------------------------------------------------
    // 🔥 Fetch Chart Data — Only Once Per (Filter + Params) Key
    // -------------------------------------------------------------
    useEffect(() => {
        if (!filters.marketplace_id) return;

        if (selectedParameterKeys.length === 0) {
            setChartData({ labels: [], datasets: [] });
            // ensure chart doesn't show spinner when there are no parameters
            setChartLoading(false);
            return;
        }

        const key = buildFilterKey(filters) + "|" + selectedParameterKeys.sort().join(",");

        if (fetchedChartKeysRef.current.has(key)) return;
        fetchedChartKeysRef.current.add(key);

        updateChartSeriesBasedOnParameters(selectedParameterKeys);

    }, [filters, selectedParameterKeys]);

    /** ----------------------------------------
     * API: Summary Tiles
     * ---------------------------------------- */
    const fetchProfitAndLossData = async () => {
        setLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/chart/get-summary-data${ConvertParams(filters)}`,
                'get',
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                setTilesList(response?.data?.summaryData || []);
            } else {
                message.warning(response?.message);
            }
        } catch (e) {
            message.error("Error fetching summary data.");
        }
        setLoading(false);
    };

    /** ----------------------------------------
     * API: Chart Data
     * ---------------------------------------- */
    const updateChartSeriesBasedOnParameters = async (params) => {
        // use chart-specific loader so summary/loading state is independent
        setChartLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/chart/get-chart-data${ConvertParams(filters)}`,
                'get',
                null,
                true,
                {},
                userData?.token
            );

            if (!response?.status) {
                setChartData({ labels: [], datasets: [] });
                return;
            }

            const responseData = response.data || [];
            const data = [...responseData].reverse();

            const labels = data.map((row) => row.report_date);

            const amountParams = [
                'sales','promo','total_tax','additional_charges','total_fba_fees',
                'amazon_fees','refund_cost','removal_shipment','ppc_costs','promotions',
                'adjustments','adhoc_disbursement','estimated_payout','debt_payment',
                'total_cogs','total_expenses','gross_profit','net_profit'
            ];

            const unitsParams = ['units_sold', 'total_refunds', 'total_sessions'];
            const ratioParams = ['profit_margin', 'total_roi'];

            const COLORS = [
                '#4285F4','#DB4437','#F4B400','#0F9D58','#AB47BC','#00ACC1','#FF7043','#9E9D24',
                '#5C6BC0','#00796B','#C2185B','#7B1FA2','#F06292','#4DB6AC','#D4E157','#FF8F00',
                '#8D6E63','#F4511E','#3949AB','#1E88E5','#43A047','#FDD835','#6D4C41','#2E7D32'
            ];

            const newSeriesDefinitions = [];

            const datasets = params.map((param, index) => {
                const isUnit = unitsParams.includes(param);
                const isRatio = ratioParams.includes(param);
                const isAmount = amountParams.includes(param);

                const type = isUnit || isRatio ? "bar" : "line";
                const yAxisID = isUnit || isRatio ? "y1" : "y";

                const color = COLORS[index % COLORS.length];

                newSeriesDefinitions.push({ paramKey: param, isUnit, isRatio, isAmount });

                return {
                    label: param.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
                    data: data.map((row) => row[param] || 0),
                    type,
                    yAxisID,
                    borderColor: type === "line" ? color : undefined,
                    backgroundColor: type === "bar" ? color : "transparent",
                    tension: 0.3,
                    pointBackgroundColor: color,
                    order: type === "line" ? 1 : 2
                };
            });

            setSeriesDefinitions(newSeriesDefinitions);
            setChartData({ labels, datasets });
        } catch (e) {
            message.error("Error fetching chart data.");
            setChartData({ labels: [], datasets: [] });
        } finally {
            // stop only chart-specific loader in all code paths
            setChartLoading(false);
        }
    };

    /** ----------------------------------------
     * Fetch Parameter Presets
     * ---------------------------------------- */
    const fetchFilterData = async () => {
        try {
            const response = await MakeApiCall(
                `dashboard/chart/get-parameters`,
                'get',
                null,
                true,
                {},
                userData?.token
            );

            if (!response?.status) {
                message.warning(response?.message);
                return;
            }

            const { default_presets, all_parameters } = response.data;

            setDefaultPresets(default_presets);

            // filter out `total_sessions` from selectable parameters
            const formattedParameters = Object.entries(all_parameters)
                .filter(([value]) => value !== 'total_sessions')
                .map(([value, label]) => ({ value, label }));
            setParameterOptions(formattedParameters);

            if (isInitialLoad.current) {
                const accountOverviewKey = Object.keys(default_presets).find(
                    (key) => default_presets[key]?.label === "Account Overview"
                );

                if (accountOverviewKey) {
                    setSelectedPreset(accountOverviewKey);
                    const presetParams = default_presets[accountOverviewKey]?.parameters || {};
                    // ensure `total_sessions` isn't accidentally set from presets
                    const paramKeys = Object.keys(presetParams).filter(k => k !== 'total_sessions');
                    setSelectedParameterKeys(paramKeys);
                }

                isInitialLoad.current = false;
            }

        } catch (e) {
            message.error("Error fetching filter configuration.");
        }
    };

    const handleParameterChange = (values) => setSelectedParameterKeys(values);

    const handlePresetChange = (presetKey) => {
        setSelectedPreset(presetKey);

        fetchedChartKeysRef.current = new Set();

        const presetParams = defaultPresets[presetKey]?.parameters || {};
        const paramKeys = Object.keys(presetParams).filter(k => k !== 'total_sessions');
        setSelectedParameterKeys(paramKeys);
    };

    /** ----------------------------------------
     * COLLAPSE SECTION (unchanged)
     * ---------------------------------------- */
    const generateCollapseItems = (summaryData) => {
        if (!summaryData || Object.keys(summaryData).length === 0)
            return { performanceMetrics: [], profitLossMetrics: [], revenueMetrics: [], costMetrics: [] };

        const latestData = summaryData;

        const sections = [
            {
                key: '1',
                title: 'Total Sales',
                amount: getAmount(latestData.sales.total, selectedCurrencySign),
                items: [
                    { label: 'Organic', value: getAmount(latestData.sales.organic, selectedCurrencySign) },
                    { label: 'Sponsored product', value: getAmount(latestData.sales.sp_sales, selectedCurrencySign) },
                    { label: 'Sponsored brand', value: getAmount(latestData.sales.sb_sales, selectedCurrencySign) },
                    { label: 'Sponsored display', value: getAmount(latestData.sales.sd_sales, selectedCurrencySign) }
                ]
            },
            {
                key: '2',
                title: 'Amazon Fees',
                amount: getAmount(latestData.amazon_fees.total, selectedCurrencySign),
                items: Object.entries(latestData.amazon_fees)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(value, selectedCurrencySign)
                    }))
            },
            {
                key: '3',
                title: 'Advertising cost',
                amount: getAmount(latestData.advertising_cost.total, selectedCurrencySign),
                items: [
                    { label: 'Sponsored Product', value: getAmount(latestData.advertising_cost.sp_cost, selectedCurrencySign) },
                    { label: 'Sponsored Brands', value: getAmount(latestData.advertising_cost.sb_cost, selectedCurrencySign) },
                    { label: 'Sponsored Display', value: getAmount(latestData.advertising_cost.sd_cost, selectedCurrencySign) },
                    { label: 'Other advertising expenses', value: getAmount(latestData.advertising_cost.external_advertising_expense, selectedCurrencySign) }
                ]
            },
            {
                key: '4',
                title: 'Cost of Goods',
                amount: getAmount(latestData.cost_of_goods.total, selectedCurrencySign),
                items: [
                    { label: 'COGS', value: getAmount(latestData.cost_of_goods.cost_of_goods_sold, selectedCurrencySign) }
                ]
            },
            {
                key: '5',
                title: 'Expenses',
                amount: getAmount(latestData.expenses.total, selectedCurrencySign),
                items: Object.entries(latestData.expenses)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(value, selectedCurrencySign)
                    }))
            },
            {
                key: '6',
                title: 'Tax Liability',
                amount: getAmount(latestData.vat, selectedCurrencySign),
                items: []
            },
            {
                key: '7',
                title: 'Gross Profit',
                amount: getAmount(latestData.gross_profit, selectedCurrencySign),
                items: []
            },
            {
                key: '8',
                title: 'Net Profit',
                amount: getAmount(latestData.net_profit, selectedCurrencySign),
                items: []
            },
            {
                key: '9',
                title: 'Payments',
                amount: getAmount(latestData.estimated_payout, selectedCurrencySign),
                items: []
            },
            {
                key: '10',
                title: 'Advertising clicks',
                amount: `${latestData.advertising_clicks.total || 0}`,
                items: [
                    { label: 'Sponsored product', value: latestData.advertising_clicks.sp_click },
                    { label: 'Sponsored brand', value: latestData.advertising_clicks.sb_click },
                    { label: 'Sponsored display', value: latestData.advertising_clicks.sd_click }
                ]
            },
            {
                key: '11',
                title: 'Total units',
                amount: `${latestData.units.total || 0}`,
                items: [
                    { label: 'Organic', value: latestData.units.organic },
                    { label: 'Sponsored product', value: latestData.units.sp_units },
                    { label: 'Sponsored brand', value: latestData.units.sb_units },
                    { label: 'Sponsored display', value: latestData.units.sd_units }
                ]
            },
            {
                key: '12',
                title: 'Ad conversion',
                amount: `${
                    latestData.advertising_clicks.total > 0
                        ? (
                            (
                                latestData.units.sp_units +
                                latestData.units.sb_units +
                                latestData.units.sd_units
                            ) /
                            latestData.advertising_clicks.total
                        ).toFixed(2)
                        : "0"
                }%`,
                items: []
            },
            {
                key: '13',
                title: 'Refunds %',
                amount: `${(latestData.refunds_percent || 0).toFixed(2)}%`,
                items: []
            }
        ];

        const formatSections = (items) =>
            items.map((section) => ({
                key: section.key,
                label: (
                    <div className="d-flex justify-content-between w-100">
                        <span className="text-gray-800 fw-bold fs-6">{section.title}</span>
                        <span className="text-gray-800 fw-bolder fs-6 me-3 gotham_black">
                            {section.amount}
                        </span>
                    </div>
                ),
                children:
                    section.items.length > 0 ? (
                        <>
                            {section.items.map((item, i) => (
                                <React.Fragment key={i}>
                                    <div className="row g-4 mt-0 ps-9">
                                        <div className="col w-md-100 d-flex align-items-center justify-content-between pe-md-0 mt-1">
                                            <h3 className="mb-1 fw-semibold text-gray-700 fs-6">{item.label}</h3>
                                            <h3 className="fs-6 text-gray-800 fw-bolder mb-0">{item.value}</h3>
                                        </div>
                                    </div>
                                    {i < section.items.length - 1 && (
                                        <div className="separator separator-dashed border-gray-300 my-2" />
                                    )}
                                </React.Fragment>
                            ))}
                        </>
                    ) : null,
                showArrow: section.items.length > 0,
                collapsible: section.items.length > 0 ? "" : "disabled",
                className: section.items.length === 0 ? "ps-9" : ""
            }));

        return {
            revenueMetrics: formatSections(sections.slice(0, 1)),
            costMetrics: formatSections(sections.slice(1, 6)),
            profitLossMetrics: formatSections(sections.slice(6, 9)),
            performanceMetrics: formatSections(sections.slice(9, 13))
        };
    };

    const { performanceMetrics, profitLossMetrics, revenueMetrics, costMetrics } =
        generateCollapseItems(tilesList);

    /** ----------------------------------------
     * Chart Options (unchanged)
     * ---------------------------------------- */
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        let label = ctx.dataset.label + ": ";
                        const val = ctx.parsed.y;
                        const series = seriesDefinitions[ctx.datasetIndex];

                        if (series?.isUnit) return label + val.toFixed(0) + " Units";
                        if (series?.isRatio) return label + val.toFixed(2) + "%";
                        if (series?.isAmount) return label + getAmount(val, selectedCurrencySign);

                        return label + val;
                    }
                }
            }
        },
        scales: {
            x: { type: "category" },
            y: {
                type: "linear",
                position: "left",
                ticks: {
                    callback: (v) => getAmount(v, selectedCurrencySign)
                }
            },
            y1: {
                type: "linear",
                position: "right",
                grid: { drawOnChartArea: false },
                title: { display: true, text: "Units / ROI / Margin" }
            }
        }
    };

    const CustomPrevArrow = (props) => (
        <CaretLeftFilled
            className={props.className}
            style={{ fontSize: '40px', color: '#181C31', zIndex: 1, left: '-10px' }}
            onClick={props.onClick}
        />
    );

    const CustomNextArrow = (props) => (
        <CaretRightFilled
            className={props.className}
            style={{ fontSize: '40px', color: '#181C31', zIndex: 1, right: '15px' }}
            onClick={props.onClick}
        />
    );

    return (
        <Wrapper>
            <div className="row g-5 g-xl-5 dashboard-cus-row ">
                <div className="col-xxl-8 mb-5 mb-xl-5 ">
                    <div className="card ">
                        <div className="card-header ">
                            <h3 className="card-title align-items-start flex-column">
                                <span className="card-label fw-bold text-dark">Overview</span>
                            </h3>
                            <div className="card-toolbar">
                                <div className="d-flex responsive-select-container">
                                    <Select
                                        placeholder="Select Preset"
                                        className="me-2 custom-select-width"
                                        size="large"
                                        value={selectedPreset}
                                        onChange={handlePresetChange}
                                    >
                                        {Object.entries(defaultPresets).map(([key, preset]) => (
                                            <Option key={key} value={key}>
                                                {preset.label}
                                            </Option>
                                        ))}
                                    </Select>

                                    <Select
                                        mode="multiple"
                                        size="large"
                                        className="custom-select-width"
                                        placeholder="Select Parameters"
                                        value={selectedParameterKeys}
                                        onChange={handleParameterChange}
                                        options={parameterOptions}
                                        maxTagCount={0}
                                        maxTagPlaceholder={() =>
                                            selectedParameterKeys.length > 0
                                                ? `${selectedParameterKeys.length} selected`
                                                : 'No parameter selected'
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card-body pb-0">
                            {(loading || chartLoading) ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '400px'
                                }}>
                                    <Spin size="large" tip="Loading..." />
                                </div>
                            ) : (
                                <div style={{ height: '400px' }}>
                                    <Chart type="bar" options={chartOptions} data={chartData} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xxl-4 mb-md-5 mb-xl-5">
                    <div className="card card-flush h-md-100">
                        <div className="card-header p-3 px-5" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="border-till border rounded p-4 px-5 mb-3"
                                 style={{ borderColor: '#63CE94 !important', background: '#d1e3f6' }}>
                                <div className="d-flex align-items-center justify-content-center">
                                    <h5 className="mb-0 text-green fw-semibold ms-3 fs-4">Sales Overview</h5>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0 px-5 pb-0">
                            <Carousel
                                dots={false}
                                arrows
                                prevArrow={<CustomPrevArrow />}
                                nextArrow={<CustomNextArrow />}
                            >

                                {/* Revenue */}
                                <div>
                                    <div className="border-till border rounded p-3 px-4 mb-3"
                                         style={{ borderColor: '#63CE94 !important', background: '#fff4e6' }}>
                                        <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Revenue</h5>
                                    </div>
                                    <div className="carousel-content-scrollable">
                                        <Collapse items={revenueMetrics} />
                                    </div>
                                </div>

                                {/* Cost */}
                                <div>
                                    <div className="border-till border rounded p-3 px-4 mb-3"
                                         style={{ borderColor: '#63CE94 !important', background: '#fff4e6' }}>
                                        <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Cost</h5>
                                    </div>
                                    <div className="carousel-content-scrollable">
                                        <Collapse items={costMetrics} />
                                    </div>
                                </div>

                                {/* Profit */}
                                <div>
                                    <div className="border-till border rounded p-3 px-4 mb-3"
                                         style={{ borderColor: '#63CE94 !important', background: '#fff4e6' }}>
                                        <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Profit</h5>
                                    </div>
                                    <div className="carousel-content-scrollable">
                                        <Collapse items={profitLossMetrics} />
                                    </div>
                                </div>

                                {/* Performance */}
                                <div>
                                    <div className="border-till border rounded p-3 px-4 mb-3"
                                         style={{ borderColor: '#63CE94 !important', background: '#fff4e6' }}>
                                        <h5 className="mb-0 text-green fw-semibold fs-5 text-center">Performance</h5>
                                    </div>
                                    <div className="carousel-content-scrollable">
                                        <Collapse items={performanceMetrics} />
                                    </div>
                                </div>

                            </Carousel>
                        </div>

                        <div className="card-footer p-3 px-5">
                            <div className="border border-till border-dashed rounded p-4 px-5 mb-3"
                                 style={{
                                     borderColor: '#63CE94 !important',
                                     background: '#63ce9440'
                                 }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0 text-green fw-semibold ms-3 fs-4">Net Profit</h5>
                                    <h4 className="mb-0 fw-bolder text-green fs-3">
                                        {selectedCurrencySign}{tilesList?.net_profit?.toFixed(2) || '0.00'}
                                    </h4>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <ItemMaster
                selectedMarketplace={selectedMarketplace}
                selectedCurrencySign={selectedCurrencySign}
                userData={userData}
                selectedTileData={filters}
            />
        </Wrapper>
    );
};

export default ChartComponent;

